import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { ScrollArea } from "@/components/ui/scroll-area";
import SwipeableQueueItem from "./AudioPlayer/SwipeableQueueItem";
import LoadMoreItem from "./AudioPlayer/LoadMoreItem";
import { useAudioPlayer } from "@/contexts/AudioPlayerContext";
import LoadingScreen from "./ui/loading-screen";
import { podcastService } from "@/lib/podcastService";
import { useAuth } from "@/contexts/AuthContext";
import type { Podcast } from "@/lib/podcastService";
import { Button } from "./ui/button";
import { RefreshCw } from "lucide-react";
import { useToast } from "@/components/ui/use-toast";
import { supabase } from "@/lib/supabase";

interface Episode {
  id: string;
  title: string;
  subtitle?: string;
  imageUrl: string;
  duration: string;
  audioUrl: string;
  podcastName?: string;
  podcastId?: string;
  publishedAt?: Date;
  listens?: number;
  likes?: number;
}

const INITIAL_QUEUE_SIZE = 7;
const LOAD_MORE_INCREMENT = 7;

// Simple in-memory cache
const cache = {
  listenHistory: {
    data: [] as string[],
    timestamp: 0,
    userId: null as string | null
  },
  podcasts: {
    data: [] as any[],
    timestamp: 0,
    feedType: null as string | null,
    userId: null as string | null
  }
};

const CACHE_EXPIRY = 5 * 60 * 1000; // 5 minutes

function Home() {
  const [isLoading, setIsLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [feedType, setFeedType] = useState<"following" | "for-you">("for-you");
  const { user } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  const loadingRef = useRef(false);
  
  // Add a key to force complete remount when feed type changes
  const [feedKey, setFeedKey] = useState(0);
  const [listenHistory, setListenHistory] = useState<string[]>([]);
  const [historyLoaded, setHistoryLoaded] = useState(false);
  const [allEpisodes, setAllEpisodes] = useState<Episode[]>([]);
  const [displayIndex, setDisplayIndex] = useState(INITIAL_QUEUE_SIZE);

  const {
    currentEpisode,
    isEpisodeBookmarked,
    playEpisode,
    toggleBookmark,
    toggleFavorite,
    queueEpisodes,
    setQueueEpisodes,
    displayedEpisodes,
    setDisplayedEpisodes,
    setOnEndedCallback,
    setAllEpisodes: setContextAllEpisodes,
    isPlaying,
    togglePlay,
  } = useAudioPlayer();

  // Memoize listen history fetching
  const fetchListenHistory = useCallback(async () => {
    // Check cache first
    if (
      cache.listenHistory.userId === user?.id && 
      Date.now() - cache.listenHistory.timestamp < CACHE_EXPIRY
    ) {
      return cache.listenHistory.data;
    }

    if (!user) return [];

    try {
      if (!supabase) {
        console.error("Supabase client is not initialized");
        return [];
      }

      const { data: historyData } = await supabase
        .from("listen_history")
        .select("episode_id")
        .eq("user_id", user.id);

      const historyEpisodeIds = historyData?.map(entry => entry.episode_id) || [];

      // Update cache
      cache.listenHistory = {
        data: historyEpisodeIds,
        timestamp: Date.now(),
        userId: user.id
      };

      return historyEpisodeIds;
    } catch (error) {
      console.error("Listen history fetch error:", error);
      return [];
    }
  }, [user]);

  // Memoize podcast fetching logic
  const fetchPodcasts = useCallback(async () => {
    // Check cache first
    if (
      cache.podcasts.feedType === feedType && 
      cache.podcasts.userId === user?.id &&
      Date.now() - cache.podcasts.timestamp < CACHE_EXPIRY
    ) {
      return cache.podcasts.data;
    }

    try {
      let podcasts: any[] = [];
      if (feedType === "following" && user) {
        const { data: subscriptions } = await supabase
          .from("user_subscriptions")
          .select("podcast_id")
          .eq("user_id", user.id);

        if (!subscriptions?.length) return [];

        const podcastIds = subscriptions.map((sub) => sub.podcast_id);

        const { data: subscribedPodcasts } = await supabase
          .from("podcasts")
          .select("*")
          .in("id", podcastIds)
          .order("created_at", { ascending: false });

        podcasts = subscribedPodcasts || [];
      } else {
        const { data: allPodcasts } = await supabase
          .from("podcasts")
          .select("*")
          .order("created_at", { ascending: false });

        podcasts = allPodcasts || [];
      }

      // Update cache
      cache.podcasts = {
        data: podcasts,
        timestamp: Date.now(),
        feedType,
        userId: user?.id || null
      };

      return podcasts;
    } catch (error) {
      console.error("Podcast fetch error:", error);
      return [];
    }
  }, [feedType, user]);

  // Helper function to validate episode with more detailed logging
  const isValidEpisode = (episode: any): boolean => {
    if (!episode) {
      console.warn('Invalid episode: null or undefined');
      return false;
    }

    const validationChecks = [
      { check: !!episode.id, message: 'Missing episode ID' },
      { check: !!episode.title, message: 'Missing episode title' },
      { check: !!episode.audioUrl, message: 'Missing audio URL' },
      { check: episode.audioUrl !== 'unknown', message: 'Audio URL is "unknown"' }
    ];

    const failedChecks = validationChecks.filter(validation => !validation.check);

    if (failedChecks.length > 0) {
      console.warn('Episode validation failed:', failedChecks.map(check => check.message));
      return false;
    }

    return true;
  };

  // Improved sorting function to distribute episodes across podcasts
  const sortEpisodesWithDiversity = (episodes: Episode[]): Episode[] => {
    // First, separate episodes by likes and podcast
    const likedEpisodes = episodes.filter(ep => (ep.likes || 0) > 0);
    const unlikedEpisodes = episodes.filter(ep => (ep.likes || 0) === 0);

    // Group unliked episodes by podcast
    const podcastGroups = new Map<string, Episode[]>();
    unlikedEpisodes.forEach(episode => {
      const podcastId = episode.podcastId || 'unknown';
      if (!podcastGroups.has(podcastId)) {
        podcastGroups.set(podcastId, []);
      }
      podcastGroups.get(podcastId)!.push(episode);
    });

    // Sort liked episodes with more complex sorting
    const sortedLikedEpisodes = likedEpisodes.sort((a, b) => {
      // First, sort by number of likes
      const likeDiff = (b.likes || 0) - (a.likes || 0);
      if (likeDiff !== 0) return likeDiff;

      // If likes are equal, add randomness
      // Use podcast diversity as a secondary sorting criteria
      const aPodcastWeight = Math.random() * 100;
      const bPodcastWeight = Math.random() * 100;
      return bPodcastWeight - aPodcastWeight;
    });

    // Distribute unliked episodes across the result
    const result: Episode[] = [...sortedLikedEpisodes];
    const podcastGroupsArray = Array.from(podcastGroups.entries());
    
    // Randomize podcast order to prevent always showing the same podcast
    podcastGroupsArray.sort(() => Math.random() - 0.5);

    let currentPodcastIndex = 0;
    while (result.length < episodes.length) {
      const currentPodcastGroup = podcastGroupsArray[currentPodcastIndex];
      if (currentPodcastGroup && currentPodcastGroup[1].length > 0) {
        result.push(currentPodcastGroup[1].shift()!);
      }
      
      // Move to next podcast, wrap around if needed
      currentPodcastIndex = (currentPodcastIndex + 1) % podcastGroupsArray.length;
    }

    return result;
  };

  // New helper function for formatting episode subtitles
  function formatSubtitle(description?: string): string {
    if (!description) return "";
    return description.substring(0, 120) + "...";
  }

  // New helper function to fetch episodes for a given podcast
  async function fetchEpisodesForPodcast(podcast) {
    try {
      const podcastData = await podcastService.getPodcast(podcast.id);
      if (!podcastData?.episodes) return [];
      return podcastData.episodes
        .filter(episode => episode?.id && episode?.title && episode?.audioUrl && episode.audioUrl !== 'unknown')
        .map(episode => ({
           id: episode.id,
           title: episode.title,
           subtitle: formatSubtitle(episode.description),
           imageUrl: episode.imageUrl || '',
           duration: episode.duration || '00:00',
           audioUrl: episode.audioUrl,
           podcastName: podcast.name,
           podcastId: podcast.id,
           publishedAt: episode.publishedAt,
           listens: episode.listen_count || 0,
           likes: episode.like_count || 0,
        }));
    } catch (error) {
      console.error(`Error fetching episodes for podcast ${podcast.id}:`, error);
      return [];
    }
  }

  // Load listen history first
  useEffect(() => {
    const loadHistory = async () => {
      try {
        const historyIds = await fetchListenHistory();
        setListenHistory(historyIds);
        setHistoryLoaded(true);
      } catch (error) {
        console.error("Error loading listen history:", error);
        setHistoryLoaded(true); // Still mark as loaded even on error
      }
    };

    setHistoryLoaded(false);
    loadHistory();
  }, [feedType, feedKey, fetchListenHistory]);

  // Then load feed once history is loaded
  useEffect(() => {
    if (!historyLoaded) return;

    const loadFeedData = async () => {
      if (loadingRef.current) return;

      try {
        loadingRef.current = true;
        setIsLoading(true);

        // Reset state
        setQueueEpisodes([]);
        setDisplayedEpisodes([]);
        setAllEpisodes([]);

        let podcasts = await fetchPodcasts();
        if (!podcasts || podcasts.length === 0) {
          return;
        }

        console.log('Loading feed with listen history:', listenHistory);

        // Fetch episodes for each podcast
        const podcastEpisodePromises = podcasts.map(podcast => fetchEpisodesForPodcast(podcast));
        const allEpisodeArrays = await Promise.all(podcastEpisodePromises);
        const rawEpisodes = allEpisodeArrays.flat();

        // Filter out history episodes
        const nonHistoryEpisodes = rawEpisodes.filter(
          episode => !listenHistory.includes(episode.id)
        );

        console.log('Total episodes:', rawEpisodes.length);
        console.log('Episodes after history filter:', nonHistoryEpisodes.length);

        // Sort and set episodes
        const sortedEpisodes = sortEpisodesWithDiversity(nonHistoryEpisodes);
        setAllEpisodes(sortedEpisodes);
        setContextAllEpisodes(sortedEpisodes);

        // Set initial queue
        const initialEpisodes = sortedEpisodes.slice(0, INITIAL_QUEUE_SIZE);
        setQueueEpisodes(initialEpisodes);
        setDisplayedEpisodes(initialEpisodes);
        setDisplayIndex(INITIAL_QUEUE_SIZE);

        // Set the first episode as the current episode without playing it
        if (initialEpisodes.length > 0 && !currentEpisode) {
          playEpisode(initialEpisodes[0], false, { 
            skipHistoryRecord: true
          });
          // Pause immediately to prevent autoplay
          setTimeout(() => {
            if (isPlaying) {
              togglePlay();
            }
          }, 100);
        }

      } catch (error) {
        console.error("Feed loading error:", error);
        toast({
          title: "Error",
          description: "Could not load feed",
          variant: "destructive",
        });
      } finally {
        loadingRef.current = false;
        setIsLoading(false);
      }
    };

    loadFeedData();
  }, [historyLoaded, feedType, feedKey]);

  const loadMoreEpisodes = useCallback(() => {
    const nextIndex = displayIndex + LOAD_MORE_INCREMENT;
    const nextEpisodes = allEpisodes.slice(0, nextIndex);
    
    setQueueEpisodes(nextEpisodes);
    setDisplayedEpisodes(nextEpisodes);
    setDisplayIndex(nextIndex);
  }, [allEpisodes, displayIndex]);

  // Set up track ended callback
  useEffect(() => {
    const handleNextTrack = () => {
      if (queueEpisodes.length === 0) return;

      const [nextEpisode, ...remainingQueue] = queueEpisodes;
      if (nextEpisode) {
        setQueueEpisodes(remainingQueue);
        setDisplayedEpisodes((prev) =>
          prev.filter((ep) => ep.id !== nextEpisode.id),
        );
        playEpisode(nextEpisode, true); // Mark as auto-play
      }
    };

    setOnEndedCallback(handleNextTrack);
  }, [queueEpisodes, playEpisode, setOnEndedCallback]);

  const handleRefresh = async () => {
    if (refreshing) return;
    setRefreshing(true);
    // Force a complete remount by changing the key
    setFeedKey(prev => prev + 1);
    setRefreshing(false);
  };

  const handleFeedTypeChange = (type: "following" | "for-you") => {
    if (type === "following" && !user) {
      navigate("/login");
      toast({
        title: "Login Required",
        description: "Please log in to access the Following feed",
        variant: "default",
      });
      return;
    }

    if (feedType !== type) {
      // Force a complete reset when changing feed type
      setFeedKey(prev => prev + 1);
      setFeedType(type);
    }
  };

  const handleEpisodeClick = (episode: Episode) => {
    playEpisode(episode, false, { resetHistory: true }); // Reset history on manual play
  };

  const handleInteraction = (action: () => void) => {
    if (!user) {
      navigate("/login");
      return;
    }
    action();
  };

  const removeFromQueue = (episodeId: string) => {
    const updatedQueue = queueEpisodes.filter((ep) => ep.id !== episodeId);
    setQueueEpisodes(updatedQueue);
    setDisplayedEpisodes((prev) => prev.filter((ep) => ep.id !== episodeId));
  };

  if (isLoading) {
    return <LoadingScreen />;
  }

  return (
    <div
      key={feedKey}
      className="w-full h-full bg-white/5 backdrop-blur-md rounded-xl border border-white/20 overflow-hidden flex flex-col"
      role="region"
      aria-label="Up Next Queue"
    >
      <div className="flex-none px-4 py-2 border-b border-white/20">
        <div className="flex justify-between items-center">
          <h2 className="text-sm font-medium text-gray-800">Up Next</h2>
          <div className="flex rounded-md border border-[#735fe2] p-0.5 w-[152px] bg-white/5">
            <button
              onClick={() => handleFeedTypeChange("for-you")}
              className={`flex-1 px-2.5 py-1 rounded text-xs font-medium transition-all ${feedType === "for-you" ? "bg-gradient-to-r from-[#735fe2] to-[#627ae2] text-white" : "text-[#735fe2] bg-white/0 hover:bg-white/20"}`}
            >
              For You
            </button>
            <button
              onClick={() => handleFeedTypeChange("following")}
              className={`flex-1 px-2.5 py-1 rounded text-xs font-medium transition-all ${feedType === "following" ? "bg-gradient-to-r from-[#735fe2] to-[#627ae2] text-white" : "text-[#735fe2] bg-white/0 hover:bg-white/20"}`}
            >
              Following
            </button>
          </div>
        </div>
      </div>

      <ScrollArea className="flex-1">
        {displayedEpisodes.length > 0 ? (
          <div className="divide-y divide-white/5">
            {displayedEpisodes.map((episode) => (
              <SwipeableQueueItem
                key={episode.id}
                title={episode.title}
                duration={episode.duration}
                imageUrl={episode.imageUrl}
                listens={episode.listens}
                likes={episode.likes}
                isActive={episode.id === currentEpisode?.id}
                isBookmarked={isEpisodeBookmarked(episode.id)}
                onClick={() => handleEpisodeClick(episode)}
                onPodcastClick={() => {
                  if (episode.podcastId) {
                    navigate(`/podcast/${episode.podcastId}`);
                  }
                }}
                onBookmark={() =>
                  handleInteraction(() => toggleBookmark(episode.id))
                }
                onDelete={() => removeFromQueue(episode.id)}
              />
            ))}
            {allEpisodes.length > displayedEpisodes.length && (
              <LoadMoreItem 
                onClick={loadMoreEpisodes} 
              />
            )}
          </div>
        ) : feedType === "following" ? (
          <div className="h-full flex items-center justify-center p-6">
            <div className="text-center">
              <p className="text-gray-600 mb-4">
                You're not following any podcasts yet. 
                Discover and subscribe to podcasts to fill your Following feed!
              </p>
              <Button
                onClick={() => navigate('/search')}
                className="bg-gradient-to-r from-[#735fe2] to-[#627ae2] text-white"
              >
                Discover Podcasts
              </Button>
            </div>
          </div>
        ) : (
          <div className="h-full flex items-center justify-center p-6">
            <div className="text-center">
              <p className="text-gray-600 mb-4">
                No clips in your feed. Try refreshing or check back later.
              </p>
              <Button
                onClick={handleRefresh}
                className="bg-gradient-to-r from-[#735fe2] to-[#627ae2] text-white"
              >
                Refresh Feed
              </Button>
            </div>
          </div>
        )}
      </ScrollArea>
    </div>
  );
}

export default Home;
